import { WorkEmpty } from '../../components/work-page.tsx';
import { WorkPage } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/admin.ts';
import ImpersonateUsers from '../../components/admin/impersonate-users.tsx';
import { Alert, Button, LoadingBlock, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import type { AdminCompanySummary } from '@shared/types/admin.ts';

/**
 * Impersonation in two steps: find the client, then pick the person.
 *
 * A page of its own rather than only a card on the company detail view,
 * because this is how the tool is actually reached — somebody is on the phone
 * describing what they can see, and the operator wants to be looking at the
 * same screen without first navigating the CMS. The same picker lives on the
 * company page for when you are already there.
 */

export default function AdminImpersonatePage() {
  useDocumentTitle('Impersonate · Admin');

  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<AdminCompanySummary | null>(null);

  const companies = useQuery({
    queryKey: ['admin', 'companies', { q: search, sort: 'last_activity', page: 1, impersonate: true }],
    queryFn: ({ signal }) =>
      adminApi.listCompanies({ q: search, sort: 'last_activity', page: 1, pageSize: 10 }, signal),
    // Only once something has been typed: the whole list is not a useful
    // answer to "who am I looking for", and this page is reached with a
    // specific company already in mind.
    enabled: search !== '',
    // Deliberately NO `keepPreviousData` here, unlike the companies list.
    // Holding the previous search's rows on screen while the next one loads is
    // right for a paged table and wrong for this: the rows are buttons that
    // pick who to sign in as, and a fast second search would leave the old
    // company's button live under the new query's heading.
  });

  return (
    <div className="portal-page">
      <WorkPage
        title="Impersonate"
        description="Sign into a client's account as one of their users to see exactly what they see. The session lasts an hour and is recorded in the admin trail only; the client does not see it."
      >
        <form
          className="mb-4 flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            setSelected(null);
            setSearch(q.trim());
          }}
        >
          <div className="min-w-56 flex-1">
            <TextInput
              label="Find a client"
              placeholder="Company, owner name, email or phone"
              value={q}
              onChange={(event) => setQ(event.target.value)}
            />
          </div>
          <div className="pb-4">
            <Button type="submit" variant="primary" disabled={q.trim() === ''}>
              Search
            </Button>
          </div>
        </form>

        {selected ? (
          <div className="card">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-lg font-semibold">{selected.name}</h2>
                <p className="text-sm text-[color:var(--color-muted)]">{selected.ownerEmail ?? '—'}</p>
              </div>
              <Button variant="ghost" onClick={() => setSelected(null)}>
                Choose another client
              </Button>
            </div>
            <ImpersonateUsers companyId={selected.id} companyName={selected.name} />
          </div>
        ) : search === '' ? (
          <WorkEmpty title="Search for a client">
            Type a company name, or an owner&apos;s name, email address or phone number, to get started.
          </WorkEmpty>
        ) : companies.isError ? (
          <Alert tone="error">
            That search could not be run.{' '}
            <Button variant="ghost" onClick={() => void companies.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : companies.isLoading ? (
          <LoadingBlock label="Searching…" />
        ) : (companies.data?.items.length ?? 0) === 0 ? (
          <WorkEmpty title="No clients match">Try a different name or address.</WorkEmpty>
        ) : (
          <div className="card p-0">
            <ul className="divide-y divide-[color:var(--color-line)]">
              {(companies.data?.items ?? []).map((company) => (
                <li key={company.id}>
                  <button
                    type="button"
                    className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[color:var(--color-surface)]"
                    onClick={() => setSelected(company)}
                  >
                    <span className="min-w-0">
                      <span className="block font-semibold text-[color:var(--color-brand)]">{company.name}</span>
                      <span className="block truncate text-xs text-[color:var(--color-muted)]">
                        {company.ownerEmail ?? '—'} · {company.seats} {company.seats === 1 ? 'user' : 'users'}
                      </span>
                    </span>
                    <span className="text-sm text-[color:var(--color-muted)]">{company.billingStatus}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </WorkPage>
    </div>
  );
}
